<template>
  <div class="order-summary">
    <SummaryBackground />
    <SummaryHeader />
    <SummaryStatus :summaryStatusData="statusData" />
    <SummaryTable :summaryData="orderData" />
    <ButtonBase
      @onButtonClick="handleButtonClick"
      :isDisabled="isOrderLoading"
      v-if="shouldShowButton"
    >
      {{ returnButtonLabel }}
    </ButtonBase>
  </div>
</template>

<script setup>
import { ref, toRefs, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useTitle } from "@vueuse/core";
import { camelize } from "@/composables/helpers/useStringHelpers.js";
import useApiHelpers from "@/composables/api/helpers";

import { useGlobalStore } from "@/stores/globalStore";

import SummaryBackground from "@/components/layout/summary/SummaryBackground.vue";
import SummaryHeader from "@/components/layout/summary/SummaryHeader.vue";
import SummaryStatus from "@/components/layout/summary/SummaryStatus.vue";
import SummaryTable from "@/components/layout/summary/SummaryTable.vue";
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";

const store = useGlobalStore();
const router = useRouter();
const route = useRoute();

const merchantSettings = toRefs(store.getMerchantSettings);
const shouldShowButton = computed(() => !store.getIsGreyLabel);

const statusData = ref({
  // defaults
  payment: {
    label: "Pending",
    title: "Payment",
    color: "#e9ae6a",
    textstatus: "pending",
    icon: "StatusPending",
  },
  identity: {
    label: "Pending",
    title: "Identity",
    color: "#e9ae6a",
    textstatus: "pending",
    icon: "StatusPending",
  },
  fulfillment: {
    label: "Pending",
    title: "Fulfilment",
    color: "#e9ae6a",
    textstatus: "pending",
    icon: "StatusPending",
  },
});
const orderData = ref({});
const returnButtonLabel = ref("");
const merchantReturnURL = ref({
  successURL: "",
  cancelURL: "",
  failedURL: "",
});
const isOrderLoading = ref(false);
const maxRepollAttempts = 2;
let attempts = 0;
let response;
let checkInterval;

const orderCancelledStatus = ["EXPIRED", "CANCELED"];
const orderFailedStatus = ["DECLINED", "FRAUD", "REFUNDED"];

const { getRequest } = useApiHelpers();

const handleButtonClick = () => {
  if (
    merchantReturnURL.value.cancelURL !== "" &&
    orderCancelledStatus.includes(orderData.value.status.toUpperCase())
  ) {
    redirectToUrl(merchantReturnURL.value.cancelURL);
    return;
  }

  if (
    merchantReturnURL.value.failedURL !== "" &&
    orderFailedStatus.includes(orderData.value.status.toUpperCase())
  ) {
    redirectToUrl(merchantReturnURL.value.failedURL);
    return;
  }

  if (merchantReturnURL.value.successURL !== "") {
    redirectToUrl(merchantReturnURL.value.successURL);
    return;
  }

  router.push("/");
};

const redirectToUrl = (url) => {
  if (store.getIsInIFrame) {
    window.parent.location = url;
    return;
  }
  window.location = url;
};

const mapStatusConfig = [
  {
    status: "complete", // cl_core is not "completed"
    color: "#03c5b3",
    icon: "StatusCompleted",
  },
  {
    status: "pending",
    color: "#e9ae6a",
    icon: "StatusPending",
  },
  {
    status: "actionRequired",
    color: "#e9ae6a",
    icon: "StatusPending",
  },
  {
    status: "underReview",
    color: "#e9ae6a",
    icon: "StatusPending",
  },
  {
    status: "preparingOrder",
    color: "#e9ae6a",
    icon: "StatusPending",
  },
  {
    status: "processing",
    color: "#e9ae6a",
    icon: "StatusPending",
  },
  {
    status: "failed",
    color: "#EC5F59",
    icon: "StatusFailed",
  },
  {
    status: "expired",
    color: "#EC5F59",
    icon: "StatusFailed",
  },
  {
    status: "refunded",
    color: "#e9ae6a",
    icon: "StatusPending",
  },
];

const statusesToRepollFor = [
  "pending",
  "actionRequired",
  "underReview",
  "preparingOrder",
  "processing",
];

const updateSatusData = (apiData) => {
  Object.entries(apiData).forEach(([status, values]) => {
    if (values.label) {
      const config = mapStatusConfig.find(
        (configStatus) => configStatus.status === camelize(values.label)
      );
      statusData.value[status] = { ...config, ...values };
    }

    if (statusData.value[status]?.label === "Complete") {
      statusData.value[status].label = "Completed";
    }
  });
};

const loadStatus = (orderResponse) => {
  const { status, summary, partnerReturnUrl } = orderResponse.data.data;
  updateSatusData(status);
  orderData.value = summary;
  if (
    partnerReturnUrl?.data?.message &&
    partnerReturnUrl?.data?.message !== ""
  ) {
    returnButtonLabel.value = partnerReturnUrl.data.message;
    merchantReturnURL.value.successURL =
      partnerReturnUrl.data.return_url_on_success;
    merchantReturnURL.value.cancelURL =
      partnerReturnUrl.data.return_url_on_cancelled;
    merchantReturnURL.value.failedURL =
      partnerReturnUrl.data.return_url_on_failure;
  } else {
    returnButtonLabel.value = "Start a new order";
  }

  isOrderLoading.value = false;
};

const checkIfStatusRequiresRepoll = (responseData) => {
  return Object.values(responseData).some((statusKeys) =>
    statusesToRepollFor.includes(statusKeys.label.toLowerCase())
  );
};

onMounted(async () => {
  isOrderLoading.value = true;
  response = await getRequest(`/status/${route.params.orderId}`);

  if (checkIfStatusRequiresRepoll(response.data.data.status)) {
    checkInterval = setInterval(async () => {
      attempts += 1;
      response = await getRequest(`/status/${route.params.orderId}`);
      if (attempts >= maxRepollAttempts) {
        clearTimeout(checkInterval);
        loadStatus(response);
      }
    }, 2500);
  } else {
    clearTimeout(checkInterval);
    loadStatus(response);
  }
});

useTitle("Banxa - Hooray!");
</script>

<style lang="scss">
.order-summary {
  width: 100%;
  max-width: 50rem;
  padding-top: 2em;
  @include breakpoint($max-m) {
    padding: 5em 10px 0 10px;
  }

  button {
    margin-bottom: 5em;

    @include breakpoint($max-m) {
      margin-bottom: 3em;
    }
  }

  div:last-child {
    //    margin-bottom: 5em;

    @include breakpoint($max-m) {
      margin-bottom: 3em;
    }
  }
}
</style>
